import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table'
import { Badge } from '../components/ui/Badge'
import { enumsApi } from '../lib/api'
import { useLanguage } from '../contexts/LanguageContext'
import { Search, Plus, Edit, Trash2, Shield } from 'lucide-react'

const languageLabels = {
  az: 'Azərbaycan',
  ru: 'Русский',
  en: 'English',
  ko: '한국어',
  kk: 'Қазақша',
  ky: 'Кыргызча'
}

export default function WarrantyTypes() {
  const [filters, setFilters] = useState({
    search: '',
    page: 1,
    limit: 100
  })
  const [deleteId, setDeleteId] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({
    name_az: '',
    name_ru: '',
    name_en: '',
    name_ko: '',
    name_kk: '',
    name_ky: ''
  })
  const { currentLanguage } = useLanguage()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery(
    ['warranty-types', filters],
    () => enumsApi.warrantyTypes.getAll(filters),
    {
      select: (data) => data.data?.data,
      keepPreviousData: true
    }
  )

  const createMutation = useMutation(
    (data) => enumsApi.warrantyTypes.create(data),
    {
      onSuccess: () => {
        toast.success('Warranty type created successfully')
        queryClient.invalidateQueries(['warranty-types'])
        setShowForm(false)
        resetForm()
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create warranty type')
      }
    }
  )

  const updateMutation = useMutation(
    ({ id, data }) => enumsApi.warrantyTypes.update(id, data),
    {
      onSuccess: () => {
        toast.success('Warranty type updated successfully')
        queryClient.invalidateQueries(['warranty-types'])
        setShowForm(false)
        setEditingId(null)
        resetForm()
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update warranty type')
      }
    }
  )

  const deleteMutation = useMutation(
    (id) => enumsApi.warrantyTypes.delete(id),
    {
      onSuccess: () => {
        toast.success('Warranty type deleted successfully')
        queryClient.invalidateQueries(['warranty-types'])
        setDeleteId(null)
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete warranty type')
      }
    }
  )

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }))
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleEdit = (warrantyType) => {
    setEditingId(warrantyType.id)
    setFormData({
      name_az: warrantyType.name_az,
      name_ru: warrantyType.name_ru,
      name_en: warrantyType.name_en,
      name_ko: warrantyType.name_ko,
      name_kk: warrantyType.name_kk || warrantyType.name_ru,
      name_ky: warrantyType.name_ky || warrantyType.name_ru
    })
    setShowForm(true)
  }

  const resetForm = () => {
    setFormData({
      name_az: '',
      name_ru: '',
      name_en: '',
      name_ko: '',
      name_kk: '',
      name_ky: ''
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId)
    }
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading warranty types: {error.message}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Warranty Types</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage warranty types for vehicles
          </p>
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Warranty Type
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editingId ? 'Edit Warranty Type' : 'Add New Warranty Type'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Names in Different Languages</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(languageLabels).map(([lang, label]) => (
                    <div key={lang}>
                      <label className="label">
                        Name ({label}) *
                        {lang === currentLanguage && (
                          <span className="ml-2 text-xs text-primary-600 font-medium">(Current Language)</span>
                        )}
                      </label>
                      <Input
                        placeholder={lang === 'en' ? 'Manufacturer Warranty' : ''}
                        value={formData[`name_${lang}`]}
                        onChange={(e) => handleInputChange(`name_${lang}`, e.target.value)}
                        required
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowForm(false)
                    setEditingId(null)
                    resetForm()
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  loading={createMutation.isLoading || updateMutation.isLoading}
                >
                  {editingId ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Search className="h-5 w-5 mr-2" />
            Search Warranty Types
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-w-md">
            <Input
              placeholder="Search warranty types..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Warranty Types ({data?.count || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : data?.results?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.results.map((warrantyType) => (
                  <TableRow key={warrantyType.id}>
                    <TableCell>
                      <div className="flex items-center">
                        <Shield className="h-4 w-4 mr-2 text-gray-400" />
                        {warrantyType[`name_${currentLanguage}`] || warrantyType.name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={warrantyType.is_active ? 'success' : 'gray'}>
                        {warrantyType.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEdit(warrantyType)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setDeleteId(warrantyType.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Shield className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No warranty types</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating a new warranty type.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Warranty Type</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this warranty type? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <Button
                variant="outline"
                onClick={() => setDeleteId(null)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                loading={deleteMutation.isLoading}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
